import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DateField, FieldGroup, TextField } from '@kanos/ui';
import { useState } from 'react';
import { AddressFields } from './address-fields.js';
import { CountryField } from './country-field.js';
import { todayIso } from './form.js';
import { usePepFields } from './pep-fields.js';
import { personRowsClassName } from './person-fields.js';
/** Empty is "not stated": the API keeps `null`, never an empty string. */
const stated = (value) => (value.trim() === '' ? null : value.trim());
const addressOf = (address) => ({
    street: address?.street ?? '',
    postalCode: address?.postalCode ?? '',
    city: address?.city ?? '',
    countryCode: address?.country ?? '',
});
const addressInput = (address) => ({
    street: stated(address.street),
    postalCode: stated(address.postalCode),
    city: stated(address.city),
    country: stated(address.countryCode),
});
/**
 * The fields of the contracting party (§ 11 Abs. 4 GwG, ADR-026) - a person's data or a
 * company's, as the intake asks (`expects`), a person also whether politically exposed
 * (ADR-029) - for the portal and the firm app alike: `input()` for the request, `fields` for
 * the form. What § 11 Abs. 4 GwG names to be collected is required (2026-11-03); the register
 * only where the company is registered.
 */
export function usePartyFields(expects, current, 
/** Whom the questions are put to: the person itself (portal) or the firm about it. */
asked) {
    const natural = current?.kind === 'natural' ? current : undefined;
    const entity = current?.kind === 'entity' ? current : undefined;
    const [givenName, setGivenName] = useState(natural?.givenName ?? '');
    const [familyName, setFamilyName] = useState(natural?.familyName ?? '');
    const [birthDate, setBirthDate] = useState(natural?.birthDate ?? null);
    const [birthPlace, setBirthPlace] = useState(natural?.birthPlace ?? '');
    const [nationality, setNationality] = useState(natural?.nationality ?? '');
    const [address, setAddress] = useState(addressOf(natural?.address));
    const pep = usePepFields(natural?.pep ?? null, asked, 3);
    const [name, setName] = useState(entity?.name ?? '');
    const [legalForm, setLegalForm] = useState(entity?.legalForm ?? '');
    const [registerCourt, setRegisterCourt] = useState(entity?.registerCourt ?? '');
    const [registerNumber, setRegisterNumber] = useState(entity?.registerNumber ?? '');
    const [seat, setSeat] = useState(addressOf(entity?.seat));
    const [representatives, setRepresentatives] = useState((entity?.representatives ?? []).join('\n'));
    const input = () => expects === 'natural'
        ? {
            kind: 'natural',
            givenName: stated(givenName),
            familyName: stated(familyName),
            birthDate,
            birthPlace: stated(birthPlace),
            nationality: stated(nationality),
            address: addressInput(address),
            pep: pep.values,
        }
        : {
            kind: 'entity',
            name: stated(name),
            legalForm: stated(legalForm),
            registerCourt: stated(registerCourt),
            registerNumber: stated(registerNumber),
            seat: addressInput(seat),
            representatives: representatives
                .split('\n')
                .map((line) => line.trim())
                .filter(Boolean),
        };
    // Numbered sections, short fields side by side: who, where, whether politically exposed - or
    // the company, its seat, who represents it.
    const fields = expects === 'natural' ? (_jsxs(_Fragment, { children: [_jsx(FieldGroup, { title: "Person", step: 1, children: _jsxs("div", { className: personRowsClassName, children: [_jsx(TextField, { label: "Vorname", placeholder: "z. B. Erika", value: givenName, onChange: setGivenName, isRequired: true }), _jsx(TextField, { label: "Nachname", placeholder: "z. B. Mustermann", value: familyName, onChange: setFamilyName, isRequired: true }), _jsx(DateField, { label: "Geburtsdatum", value: birthDate, onChange: setBirthDate, maxValue: todayIso(), isRequired: true }), _jsx(TextField, { label: "Geburtsort", placeholder: "z. B. Berlin", value: birthPlace, onChange: setBirthPlace, isRequired: true }), _jsx(CountryField, { label: "Staatsangeh\u00F6rigkeit", value: nationality, onChange: setNationality, isRequired: true })] }) }), _jsx(FieldGroup, { title: "Wohnanschrift", step: 2, children: _jsx(AddressFields, { required: true, value: address, onChange: (patch) => setAddress({ ...address, ...patch }), autoFill: asked === 'you' }) }), pep.fields] })) : (_jsxs(_Fragment, { children: [_jsxs(FieldGroup, { title: "Unternehmen oder Organisation", step: 1, children: [_jsx(TextField, { label: "Firma oder Name", placeholder: "z. B. Musterbau GmbH", value: name, onChange: setName, isRequired: true }), _jsxs("div", { className: "grid gap-4 @lg:grid-cols-3", children: [_jsx(TextField, { label: "Rechtsform", placeholder: "z. B. GmbH", value: legalForm, onChange: setLegalForm, isRequired: true }), _jsx(TextField, { label: "Registergericht", placeholder: "z. B. Amtsgericht K\u00F6ln", value: registerCourt, onChange: setRegisterCourt }), _jsx(TextField, { label: "Registernummer", placeholder: "z. B. HRB 12345", value: registerNumber, onChange: setRegisterNumber })] })] }), _jsx(FieldGroup, { title: "Sitz", step: 2, children: _jsx(AddressFields, { required: true, value: seat, onChange: (patch) => setSeat({ ...seat, ...patch }) }) }), _jsx(FieldGroup, { title: "Vertretung", step: 3, children: _jsx(TextField, { label: "Vertretungsberechtigte", placeholder: "z. B. Max Mustermann", description: "Gesch\u00E4ftsf\u00FChrung oder gesetzliche Vertreter \u2013 eine Person je Zeile.", multiline: true, value: representatives, onChange: setRepresentatives, isRequired: true }) })] }));
    return { input, fields };
}
//# sourceMappingURL=party-fields.js.map